import { useNavigate } from "react-router-dom";
import {
    cleanupUnusedCustomFontsRoute,
    forceDeleteRoute,
    previewForceDeleteRoute,
    previewUnusedCustomFontsRoute,
    seedFeatureDBRoute,
} from "../../api/routes";
import axios from "../../api/axios";
import { toast } from "react-toastify";
import { useContext, useState } from "react";
import { TOP_LEVEL_NAV_ROUTES } from "../../config/NavRoutes";
import RangeSlider from "../../components/forms/RangeSlider/RangeSlider";
import SingleMachineCameraFeed from "../../components/FeatureControls/custom-control-components/claw-camera-feeds/SingleMachineCameraFeed";
import { AuthContext } from "../../contexts/AuthContext";
import { ACCOUNT_TYPES } from "../../helpers/AccountTypes";
import "./KokoDebugControls.scss";

function Home() {
    const navigate = useNavigate();
    const [authContext] = useContext(AuthContext);
    const [xPerc, setXPerc] = useState(50);
    const [yPerc, setYPerc] = useState(50);
    const [machineID, setMachineID] = useState(1);
    const [forceDeleteId, setForceDeleteId] = useState('');
    const [forceDeletePreview, setForceDeletePreview] = useState(null);
    const [isLoadingForceDeletePreview, setIsLoadingForceDeletePreview] = useState(false);
    const [isForceDeleting, setIsForceDeleting] = useState(false);
    const [unusedFontObjectId, setUnusedFontObjectId] = useState('');
    const [unusedFontPreview, setUnusedFontPreview] = useState(null);
    const [isLoadingUnusedFontPreview, setIsLoadingUnusedFontPreview] = useState(false);
    const [isCleaningUnusedFonts, setIsCleaningUnusedFonts] = useState(false);

    const machineLabels = {
        1: 'Left Machine (New York)',
        2: 'Right Machine (New York)',
    };

    const canForceDelete = (authContext?.userData?.userLevel || 0) >= ACCOUNT_TYPES.koko.level;
    const canManageUnusedFonts = canForceDelete;
    const selectedMachineName = machineLabels[machineID];

    const getApiErrorMessage = (error) => {
        return error?.response?.data?.error
            || error?.response?.data?.message
            || error?.message
            || 'Request failed';
    };

    const gotoSeederEditor = () => {
        navigate(TOP_LEVEL_NAV_ROUTES.FEATURE_EDITOR);
    };

    const gotoClawDebugTools = () => {
        navigate(TOP_LEVEL_NAV_ROUTES.CLAW_DEBUG_TOOLS);
    };

    const seedFeatures = async (e) => {
        e.preventDefault();

        try {
            await axios.post(seedFeatureDBRoute, {}, { withCredentials: true });
            toast.success('Features seeded');
        } catch (error) {
            toast.error(`Error - ${getApiErrorMessage(error)}`);
        }
    };

    const runClawAction = async (route, successMessage, body = {}) => {
        try {
            await axios.post(
                route,
                { machineID, ...body },
                { withCredentials: true }
            );
            toast.success(successMessage);
        } catch (error) {
            toast.error(`Error - ${getApiErrorMessage(error)}`);
        }
    };

    const lookupForceDeleteTarget = async (e) => {
        e.preventDefault();
        const objectId = forceDeleteId.trim();

        if (!objectId) {
            toast.error('Enter an ObjectId first');
            return;
        }

        setIsLoadingForceDeletePreview(true);
        setForceDeletePreview(null);

        try {
            const response = await axios.post(
                previewForceDeleteRoute,
                { objectId },
                { withCredentials: true }
            );
            setForceDeletePreview(response?.data?.target || null);
        } catch (error) {
            toast.error(`Error - ${getApiErrorMessage(error)}`);
        } finally {
            setIsLoadingForceDeletePreview(false);
        }
    };

    const handleForceDelete = async () => {
        if (!forceDeletePreview?.objectId) {
            return;
        }

        const targetName = forceDeletePreview?.primaryRecord?.name || forceDeletePreview.objectId;
        const confirmed = window.confirm(
            `Permanently delete ${targetName}? This removes the database record and all linked stored assets.`
        );

        if (!confirmed) {
            return;
        }

        setIsForceDeleting(true);

        try {
            const response = await axios.post(
                forceDeleteRoute,
                { objectId: forceDeletePreview.objectId },
                { withCredentials: true }
            );
            const result = response?.data?.result || {};
            toast.success(
                `Deleted ${result.deletedCampaignIds?.length || 0} campaign(s), ${result.deletedTemplateIds?.length || 0} template(s), and ${result.deletedCampaignAssetCount || 0} asset file(s)`
            );
            setForceDeletePreview(null);
            setForceDeleteId('');
        } catch (error) {
            toast.error(`Error - ${getApiErrorMessage(error)}`);
        } finally {
            setIsForceDeleting(false);
        }
    };

    const previewUnusedCustomFonts = async (e) => {
        e.preventDefault();

        setIsLoadingUnusedFontPreview(true);
        setUnusedFontPreview(null);

        try {
            const response = await axios.post(
                previewUnusedCustomFontsRoute,
                { objectId: unusedFontObjectId.trim() },
                { withCredentials: true }
            );
            setUnusedFontPreview(response?.data?.result || null);
        } catch (error) {
            toast.error(`Error - ${getApiErrorMessage(error)}`);
        } finally {
            setIsLoadingUnusedFontPreview(false);
        }
    };

    const cleanupUnusedCustomFonts = async () => {
        if (!unusedFontPreview) {
            return;
        }

        if ((unusedFontPreview?.unusedFontCount || 0) === 0) {
            toast.success('No unused custom fonts found');
            return;
        }

        const targetLabel = unusedFontObjectId.trim()
            ? `the selected campaign/template target (${unusedFontPreview.unusedFontCount} font entr${unusedFontPreview.unusedFontCount === 1 ? 'y' : 'ies'})`
            : `all scanned campaigns (${unusedFontPreview.unusedFontCount} font entr${unusedFontPreview.unusedFontCount === 1 ? 'y' : 'ies'})`;

        const confirmed = window.confirm(`Remove unused custom font metadata from ${targetLabel}? This updates campaign records permanently.`);
        if (!confirmed) {
            return;
        }

        setIsCleaningUnusedFonts(true);

        try {
            const response = await axios.post(
                cleanupUnusedCustomFontsRoute,
                { objectId: unusedFontObjectId.trim() },
                { withCredentials: true }
            );
            const result = response?.data?.result || {};
            toast.success(
                `Removed ${result.removedFontCount || 0} unused font entr${result.removedFontCount === 1 ? 'y' : 'ies'} across ${result.cleanedCampaignCount || 0} campaign(s)`
            );
            setUnusedFontPreview(null);
        } catch (error) {
            toast.error(`Error - ${getApiErrorMessage(error)}`);
        } finally {
            setIsCleaningUnusedFonts(false);
        }
    };

    return (
        <div className="koko-debug-container">
            <main className="koko-debug-main">
                <section className="koko-debug-hero">
                    <p className="koko-debug-eyebrow">Koko Internal Controls</p>
                    <h1>Koko Debug Tools</h1>
                    <p className="koko-debug-build">
                        Build: <strong>{process.env.REACT_APP_BUILD_TIME || 'dev (no build time set)'}</strong>
                    </p>
                </section>

                <section className="koko-debug-card koko-debug-card--claw">
                    <div className="koko-debug-card-header">
                        <div>
                            <p className="koko-debug-card-kicker">Live Machine Control</p>
                            <h2>Claw Machine Controls</h2>
                            <p className="koko-debug-card-copy">
                                Switch between left and right cabinets, view the live camera, and send quick movement and connection commands.
                            </p>
                        </div>
                        <button className="standard-button button-secondary" type="button" onClick={gotoClawDebugTools}>
                            Open Full Claw Debug
                        </button>
                    </div>

                    <div className="koko-machine-switcher" role="tablist" aria-label="Select claw machine">
                        {[1, 2].map((machineValue) => {
                            const isActive = machineID === machineValue;
                            const label = machineLabels[machineValue];

                            return (
                                <button
                                    key={machineValue}
                                    type="button"
                                    className={`koko-machine-tab ${isActive ? 'is-active' : ''}`}
                                    onClick={() => setMachineID(machineValue)}
                                >
                                    <span>{label}</span>
                                    <small>ID {machineValue}</small>
                                </button>
                            );
                        })}
                    </div>

                    <div className="koko-claw-layout">
                        <div className="koko-claw-camera-card">
                            <SingleMachineCameraFeed machineID={machineID} machineName={selectedMachineName} />
                        </div>

                        <div className="koko-claw-control-card">
                            <div className="koko-claw-control-header">
                                <h3>{selectedMachineName}</h3>
                                <p>Fine tune the next automated move before sending it to the cabinet.</p>
                            </div>

                            <div className="koko-claw-button-row">
                                <button
                                    className="standard-button"
                                    type="button"
                                    onClick={() => runClawAction('claw/connect', `${selectedMachineName} machine connected`)}
                                >
                                    Connect
                                </button>
                                <button
                                    className="standard-button"
                                    type="button"
                                    onClick={() => runClawAction('claw/disconnect', `${selectedMachineName} machine disconnected`)}
                                >
                                    Disconnect
                                </button>
                                <button
                                    className="standard-button"
                                    type="button"
                                    onClick={() => runClawAction('claw/get-versions', `${selectedMachineName} machine versions fetched`)}
                                >
                                    Get Versions
                                </button>
                            </div>

                            <RangeSlider
                                label={'X Percentage'}
                                labelNote={''}
                                setFunc={(newVal) => { setXPerc(newVal); }}
                                min={0}
                                max={100}
                                value={xPerc}
                                showCurrentValue={true}
                            />

                            <RangeSlider
                                label={'Y Percentage'}
                                labelNote={''}
                                setFunc={(newVal) => { setYPerc(newVal); }}
                                min={0}
                                max={100}
                                value={yPerc}
                                showCurrentValue={true}
                            />

                            <button
                                className="standard-button koko-claw-primary-action"
                                type="button"
                                onClick={() => runClawAction(
                                    'claw/automate_play_action',
                                    `${selectedMachineName} machine move sent`,
                                    { xPerc, yPerc }
                                )}
                            >
                                Play {selectedMachineName} Claw Move
                            </button>

                            <button
                                className="warning-button koko-claw-primary-action"
                                type="button"
                                onClick={() => runClawAction(
                                    'claw/reboot_machine',
                                    `${selectedMachineName} machine force reboot requested`
                                )}
                            >
                                Force Reboot {selectedMachineName} Machine
                            </button>
                        </div>
                    </div>
                </section>

                <div className="koko-debug-grid">
                    <section className="koko-debug-card">
                        <div className="koko-debug-card-header">
                            <div>
                                <p className="koko-debug-card-kicker">Feature Setup</p>
                                <h2>Feature Seed Tools</h2>
                                <p className="koko-debug-card-copy">
                                    Keep the feature editor and seed data tools close without the extra debug clutter.
                                </p>
                            </div>
                        </div>

                        <div className="koko-debug-action-stack">
                            <button className="standard-button" type="button" onClick={seedFeatures}>
                                Seed Features
                            </button>
                            <button className="standard-button button-secondary" type="button" onClick={gotoSeederEditor}>
                                Open Seeder Editor
                            </button>
                        </div>
                    </section>

                    {canForceDelete &&
                        <section className="koko-force-delete-panel">
                            <div className="koko-debug-card-header">
                                <div>
                                    <p className="koko-debug-card-kicker">Permanent Cleanup</p>
                                    <h2>Force Delete Campaign / Template</h2>
                                </div>
                            </div>

                            <p className="koko-force-delete-warning">
                                Koko developers only. This permanently deletes campaign/template records and removes linked stored assets from storage.
                            </p>

                            <form className="koko-force-delete-form" onSubmit={lookupForceDeleteTarget}>
                                <input
                                    className="koko-force-delete-input"
                                    type="text"
                                    value={forceDeleteId}
                                    onChange={(e) => {
                                        setForceDeleteId(e.target.value);
                                        setForceDeletePreview(null);
                                    }}
                                    placeholder="Paste campaign or template ObjectId"
                                />
                                <button className="standard-button" type="submit" disabled={isLoadingForceDeletePreview || isForceDeleting}>
                                    {isLoadingForceDeletePreview ? 'Checking...' : 'Preview Delete'}
                                </button>
                            </form>

                            {forceDeletePreview &&
                                <div className="koko-force-delete-preview">
                                    <p><strong>Found Type:</strong> {forceDeletePreview.primaryType}</p>
                                    <p><strong>Name:</strong> {forceDeletePreview.primaryRecord?.name || 'Untitled'}</p>
                                    <p><strong>ObjectId:</strong> {forceDeletePreview.objectId}</p>

                                    {forceDeletePreview.campaigns?.length > 0 &&
                                        <div className="koko-force-delete-section">
                                            <h3>Campaigns To Delete</h3>
                                            {forceDeletePreview.campaigns.map((campaign) => (
                                                <div key={campaign.id} className="koko-force-delete-item">
                                                    <p><strong>{campaign.name || 'Untitled campaign'}</strong></p>
                                                    <p>ID: {campaign.id}</p>
                                                    <p>Template Campaign: {campaign.isTemplate ? 'Yes' : 'No'}</p>
                                                    <p>Archived: {campaign.archived ? 'Yes' : 'No'}</p>
                                                    <p>Asset Files: {campaign.assetFileCount}</p>
                                                </div>
                                            ))}
                                        </div>
                                    }

                                    {forceDeletePreview.templates?.length > 0 &&
                                        <div className="koko-force-delete-section">
                                            <h3>Templates To Delete</h3>
                                            {forceDeletePreview.templates.map((template) => (
                                                <div key={template.id} className="koko-force-delete-item">
                                                    <p><strong>{template.name || 'Untitled template'}</strong></p>
                                                    <p>ID: {template.id}</p>
                                                    <p>Type: {template.templateType || 'unknown'}</p>
                                                    <p>Linked Campaign ID: {template.campaignId || 'None'}</p>
                                                    <p>Template Image: {template.templateImageStoragePath || 'None'}</p>
                                                </div>
                                            ))}
                                        </div>
                                    }

                                    <div className="koko-force-delete-actions">
                                        <button
                                            className="standard-button koko-force-delete-button"
                                            type="button"
                                            onClick={handleForceDelete}
                                            disabled={isForceDeleting || isLoadingForceDeletePreview}
                                        >
                                            {isForceDeleting ? 'Deleting...' : 'Force Delete Permanently'}
                                        </button>
                                    </div>
                                </div>
                            }
                        </section>
                    }

                    {canManageUnusedFonts &&
                        <section className="koko-force-delete-panel koko-font-cleanup-panel">
                            <div className="koko-debug-card-header">
                                <div>
                                    <p className="koko-debug-card-kicker">Permanent Cleanup</p>
                                    <h2>Unused Custom Font Scan</h2>
                                </div>
                            </div>

                            <p className="koko-force-delete-warning koko-font-cleanup-warning">
                                Koko developers only. Preview unused `customFonts` metadata for one campaign/template or scan all campaigns with custom fonts. Cleanup removes only entries with no detected references in campaign data.
                            </p>

                            <form className="koko-force-delete-form" onSubmit={previewUnusedCustomFonts}>
                                <input
                                    className="koko-force-delete-input"
                                    type="text"
                                    value={unusedFontObjectId}
                                    onChange={(e) => {
                                        setUnusedFontObjectId(e.target.value);
                                        setUnusedFontPreview(null);
                                    }}
                                    placeholder="Optional campaign/template ObjectId. Leave blank to scan all."
                                />
                                <button className="standard-button" type="submit" disabled={isLoadingUnusedFontPreview || isCleaningUnusedFonts}>
                                    {isLoadingUnusedFontPreview ? 'Scanning...' : 'Preview Unused Fonts'}
                                </button>
                            </form>

                            {unusedFontPreview &&
                                <div className="koko-force-delete-preview">
                                    <p><strong>Scope:</strong> {unusedFontPreview.scope === 'single' ? 'Single target' : 'All campaigns with custom fonts'}</p>
                                    <p><strong>Scanned Campaigns:</strong> {unusedFontPreview.scannedCampaignCount || 0}</p>
                                    <p><strong>Campaigns With Unused Fonts:</strong> {unusedFontPreview.campaignsWithUnusedFontsCount || 0}</p>
                                    <p><strong>Unused Font Entries:</strong> {unusedFontPreview.unusedFontCount || 0}</p>

                                    {(unusedFontPreview.campaignsWithUnusedFontsCount || 0) === 0 &&
                                        <p className="koko-font-cleanup-clear">No unused custom font entries found in the scanned scope.</p>
                                    }

                                    {unusedFontPreview.campaigns?.length > 0 &&
                                        <div className="koko-force-delete-section">
                                            <h3>Campaigns With Unused Custom Fonts</h3>
                                            {unusedFontPreview.campaigns.map((campaign) => (
                                                <div key={campaign.id} className="koko-force-delete-item">
                                                    <p><strong>{campaign.name || 'Untitled campaign'}</strong></p>
                                                    <p>ID: {campaign.id}</p>
                                                    <p>Template Campaign: {campaign.isTemplate ? 'Yes' : 'No'}</p>
                                                    <p>Unused Font Count: {campaign.unusedFontCount}</p>
                                                    {campaign.unusedFonts?.map((font) => (
                                                        <p key={font.id || `${font.fontFamily}-${font.fontFilename}-${font.index}`}>
                                                            Remove: {font.fontFamily || 'Unnamed font'} ({font.fontFilename || 'No filename'})
                                                        </p>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    }

                                    {(unusedFontPreview.unusedFontCount || 0) > 0 &&
                                        <div className="koko-force-delete-actions">
                                            <button
                                                className="standard-button koko-force-delete-button koko-font-cleanup-button"
                                                type="button"
                                                onClick={cleanupUnusedCustomFonts}
                                                disabled={isCleaningUnusedFonts || isLoadingUnusedFontPreview}
                                            >
                                                {isCleaningUnusedFonts ? 'Cleaning...' : 'Remove Unused Font Metadata'}
                                            </button>
                                        </div>
                                    }
                                </div>
                            }
                        </section>
                    }
                </div>
            </main>
        </div>
    );
}

export default Home;
